import { CalendarClock, FolderKanban, GraduationCap, NotebookPen, Repeat, Star, Target, Trash2 } from 'lucide-react';

import { AreaDot } from '@/modules/areas/AreaDot';
import { useAreaColor, useAreaMap } from '@/modules/areas/useAreas';
import { useLinkNames } from '@/modules/links/linkNames';
import { useTaskDialog } from '@/modules/tasks/taskDialogStore';
import { DeadlineBadge } from '@/modules/deadlines/DeadlineBadge';
import { LinkChip } from '@/ui/composites/LinkChip';
import { pfad } from '@/app/fokus';
import { cn } from '@/lib/utils/cn';
import { formatDate, formatDe, today } from '@/lib/datetime';
import { describeRrule } from '@/lib/recurrence/rruleText.de';
import { verpasst } from '@/domain/tasks/wiederholung';
import { notizVorschau } from '@/domain/notes/bloecke';
import { taskPriority } from '@/domain/priority';
import { PriorityDot } from '@/ui/composites/PriorityDot';
import type { Task } from '@/domain/types';

interface TaskRowProps {
  task: Task;
  onToggleDone: () => void;
  onDelete?: () => void;
  onToggleBigThree?: () => void;
  /** Zeigersteuerung fuer das Ziehen in den Kalender. */
  onPointerDown?: (e: React.PointerEvent) => void;
  draggable?: boolean;
  /**
   * Auf der Seite des eigenen Projekts oder Ziels ist der Hinweis darauf
   * doppelt - dann weder Projekt- noch Ziel-Plakette.
   */
  ohneVerknuepfung?: boolean;
  /** Zusaetzliche Bedienung am Zeilenende, z. B. "Einplanen" in der Planung (F-16). */
  zusatz?: React.ReactNode;
}

export function TaskRow({
  task, onToggleDone, onDelete, onToggleBigThree, onPointerDown, draggable, ohneVerknuepfung = false, zusatz,
}: TaskRowProps) {
  const areaColor = useAreaColor();
  const bereich = useAreaMap().get(task.areaId ?? '')?.name;
  const oeffnen = useTaskDialog((st) => st.open);
  const { projects, goals, subjects } = useLinkNames();
  const fach = task.subjectId ? subjects.get(task.subjectId) : undefined;
  const done = task.status === 'erledigt';
  const prioritaet = done ? undefined : taskPriority(task);

  // Wofuer ist die Aufgabe gut? (Roadmap F-11, Leitlinie 5)
  const projekt = task.projectId ? projects.get(task.projectId) : undefined;
  const ziel = !projekt && task.goalId ? goals.get(task.goalId) : undefined;
  // Wiederkehrend (F-24): Regel als Text, Verpasstes gezaehlt statt gestapelt.
  const regel = task.wiederholung ? describeRrule(task.wiederholung.rrule) : null;
  const mal = task.wiederholung ? verpasst(task, today()) : 0;

  return (
    <li
      onPointerDown={onPointerDown}
      className={cn(
        'group flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2',
        'transition-colors hover:border-line-strong',
        draggable && 'cursor-grab touch-none select-none active:cursor-grabbing',
        done && 'opacity-55',
      )}
    >
      <input
        type="checkbox"
        checked={done}
        onChange={onToggleDone}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label={done ? 'Wieder öffnen' : 'Erledigt'}
        className="size-4 shrink-0 accent-[var(--color-accent)]"
      />

      <AreaDot color={areaColor(task.areaId)} size="sm" name={bereich} />

      {/* Klick auf den Titel oeffnet die Aufgabe - in jeder Ansicht gleich. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <button
          type="button"
          onClick={() => oeffnen(task.id)}
          title="Aufgabe bearbeiten"
          className={cn(
            'min-w-0 truncate text-left text-sm hover:underline hover:decoration-line-strong',
            done && 'line-through',
          )}
        >
          {task.title}
        </button>
        {/* Notiz direkt sichtbar (F-05, Leitlinie 2) - ganz im Dialog. */}
        {task.notes && (
          <span className="flex min-w-0 items-center gap-1 text-[12px] text-ink-faint">
            <NotebookPen className="size-3 shrink-0" aria-label="Notiz" />
            <span className="truncate">{notizVorschau(task.notes)}</span>
          </span>
        )}
      </div>

      {projekt && !ohneVerknuepfung && (
        <LinkChip to={pfad.projekt(projekt.id)} icon={FolderKanban} art="Projekt" label={projekt.title} />
      )}
      {fach && <LinkChip to={pfad.fach(fach.id)} icon={GraduationCap} art="Fach" label={fach.name} />}
      {ziel && !ohneVerknuepfung && <LinkChip to={pfad.ziel(ziel.id)} icon={Target} art="Ziel" label={ziel.title} />}

      {regel && (
        <span title={regel} className="flex shrink-0 items-center gap-1 text-[11px] text-ink-faint">
          <Repeat className="size-3" aria-hidden="true" />
          <span className="sr-only">Wiederkehrend: {regel}</span>
          {mal > 0 && <span className="rounded-full bg-warn/15 px-1.5 text-warn">{mal}× verpasst</span>}
        </span>
      )}

      {/* Eingeplant (F-06) - wann ich daran arbeite, getrennt von der Deadline. */}
      {task.plannedDate && !done && (
        <span
          title={task.timeBlockEventId ? 'Eingeplant, mit Zeitblock im Kalender' : 'Eingeplant, ohne Uhrzeit'}
          className="flex shrink-0 items-center gap-1 text-[11px] tabular-nums text-ink-faint"
        >
          <CalendarClock className="size-3" aria-hidden="true" />
          <span className="sr-only">Eingeplant am</span>
          {formatDe(task.plannedDate, 'EEEEEE dd.MM.')}
        </span>
      )}

      {/* Prioritaet als Punkt - dieselbe Farbe wie im Kalender (V-03, D-03). */}
      {prioritaet && <PriorityDot priority={prioritaet} />}

      {/* Erledigt hat keine Frist mehr - dann nur noch das Datum, ruhig. */}
      {task.dueDate && (done ? (
        <span className="shrink-0 text-[11px] tabular-nums text-ink-faint">{formatDate(task.dueDate)}</span>
      ) : (
        <DeadlineBadge date={task.dueDate} time={task.dueTime} />
      ))}

      {onToggleBigThree && (
        <button
          type="button"
          onClick={onToggleBigThree}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Als eine der wichtigsten drei markieren"
          aria-pressed={Boolean(task.isBigThree)}
          className={cn(
            'grid size-7 shrink-0 place-items-center rounded-md transition-colors',
            task.isBigThree
              ? 'text-accent-text'
              : 'text-ink-faint opacity-0 hover:text-ink group-hover:opacity-100',
          )}
        >
          <Star className={cn('size-4', task.isBigThree && 'fill-current')} />
        </button>
      )}

      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Löschen"
          className="grid size-7 shrink-0 place-items-center rounded-md text-ink-faint opacity-0 transition-colors hover:text-danger group-hover:opacity-100"
        >
          <Trash2 className="size-4" />
        </button>
      )}
      {zusatz && <span className="shrink-0" onPointerDown={(e) => e.stopPropagation()}>{zusatz}</span>}
    </li>
  );
}
